import { tokenStore } from "@/auth/token-store";
import { useSessionStore } from "@/stores/session";

/** Backend API root. Bundled build → same origin at `<base>/api`. */
export const API_BASE =
  (import.meta.env.VITE_API_URL ?? import.meta.env.BASE_URL.replace(/\/+$/, "")) + "/api";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** Send this string verbatim (e.g. YAML) with the given content type. */
  rawBody?: { content: string; contentType: string };
  /** Skip the Authorization header (public endpoints). */
  anonymous?: boolean;
  /** internal */
  _retried?: boolean;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, rawBody, anonymous, _retried, headers: extraHeaders, ...init } = options;
  const { tenant, workspace } = useSessionStore.getState();

  const headers = new Headers(extraHeaders);
  headers.set("X-Tenant", tenant);
  if (workspace) headers.set("X-Workspace", workspace);

  const token = tokenStore.getAccess();
  if (token && !anonymous) headers.set("Authorization", `Bearer ${token}`);

  let payload: BodyInit | undefined;
  if (rawBody !== undefined) {
    headers.set("Content-Type", rawBody.contentType);
    payload = rawBody.content;
  } else if (body instanceof FormData) {
    payload = body;
  } else if (body !== undefined) {
    headers.set("Content-Type", "application/json");
    payload = JSON.stringify(body);
  }

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers, body: payload });

  if (res.status === 401 && token && !anonymous && !_retried) {
    const refreshed = await ensureRefreshed();
    if (refreshed) return apiRequest<T>(path, { ...options, _retried: true });
  }

  if (res.status === 429) {
    const retryAfter = res.headers.get("Retry-After") ?? "60";
    throw new ApiError(429, `Rate limit exceeded — retry in ${retryAfter}s.`);
  }

  if (!res.ok) {
    const errBody = await res.json().catch(() => null);
    const message =
      (errBody && (errBody.error || errBody.message)) || `Request failed (HTTP ${res.status})`;
    throw new ApiError(res.status, message, errBody);
  }

  if (res.status === 204) return undefined as T;
  const contentType = res.headers.get("content-type") ?? "";
  return (contentType.includes("json") ? res.json() : res.text()) as Promise<T>;
}

// ── single-flight refresh ────────────────────────────────────────────────────

let refreshInFlight: Promise<boolean> | null = null;

/** At most one `/auth/refresh` call at a time; all 401s await the same promise. */
export function ensureRefreshed(): Promise<boolean> {
  refreshInFlight ??= doRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function doRefresh(): Promise<boolean> {
  const refreshToken = tokenStore.getRefresh();
  if (!refreshToken) return false;
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) throw new Error("refresh rejected");
    const data = (await res.json()) as { accessToken: string; refreshToken?: string };
    tokenStore.set(data.accessToken, data.refreshToken);
    return true;
  } catch {
    tokenStore.clear();
    useSessionStore.getState().setUser(null);
    return false;
  }
}
